import { useRef, useEffect, useCallback } from "react";
import { PlanCard } from "./PlanCard";
import { usePricingExplorer } from "./PricingExplorerContext";
import styles from "./pricing-explorer.module.scss";
import type { PricingExplorerVariant, PricingPlanStoryblok } from "./types";

interface Props {
  plans: PricingPlanStoryblok[];
  variant: PricingExplorerVariant;
}

export const PlanCards = ({ plans, variant }: Props) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const cardRefs = useRef<(HTMLDivElement | null)[]>([]);
  const { selectedPlanIndex, selectPlan } = usePricingExplorer();
  const didInitialCenter = useRef(false);

  const setCardRef = useCallback(
    (index: number) => (el: HTMLDivElement | null) => {
      cardRefs.current[index] = el;
    },
    []
  );

  useEffect(() => {
    const container = scrollRef.current;
    if (!container) return;

    if (
      !didInitialCenter.current &&
      container.scrollWidth > container.clientWidth
    ) {
      const defaultCard = cardRefs.current[selectedPlanIndex];
      if (defaultCard) {
        defaultCard.scrollIntoView({ inline: "center", block: "nearest" });
      }
      didInitialCenter.current = true;
    }

    const handleScroll = () => {
      const containerRect = container.getBoundingClientRect();
      const containerCenter = containerRect.left + containerRect.width / 2;
      let closestIndex = 0;
      let closestDistance = Infinity;

      cardRefs.current.forEach((card, i) => {
        if (!card) return;
        const cardRect = card.getBoundingClientRect();
        const cardCenter = cardRect.left + cardRect.width / 2;
        const distance = Math.abs(containerCenter - cardCenter);
        if (distance < closestDistance) {
          closestDistance = distance;
          closestIndex = i;
        }
      });

      selectPlan(closestIndex);
    };

    container.addEventListener("scroll", handleScroll, { passive: true });
    return () => container.removeEventListener("scroll", handleScroll);
  }, [selectPlan, selectedPlanIndex]);

  return (
    <div className={styles.planCards} ref={scrollRef}>
      {plans.map((plan, index) => (
        <div
          key={plan._uid}
          ref={setCardRef(index)}
          className={styles.planCardSlide}
        >
          <PlanCard blok={plan} index={index} variant={variant} />
        </div>
      ))}
    </div>
  );
};
